import { create } from "zustand";
import {
  LOCAL_PROVISIONAL_TTL_MS,
  type ChatEvent,
  type ChatEventsCoverage,
  type ProvisionalChatEvent,
} from "@rivets/core";

/** Cap on in-memory events retained per open chat. Once exceeded we trim the
 * oldest TRIM_COUNT events rather than let a long-lived chat grow forever;
 * the trimmed range is paged back in with `beforeSeq` like any other history. */
const MAX_EVENTS = 10_000;
const TRIM_COUNT = 2_000;
export const MAX_RETAINED_CHATS = 20;

/**
 * Where the stream on screen came from, for the `cached → synchronizing →
 * live` rendering: `cached` is history read from this browser's own store with
 * no subscription behind it yet, `synchronizing` is a subscription whose replay
 * has not answered, and `live` is a stream the relay is feeding.
 */
export type ChatSyncState = "cached" | "synchronizing" | "live";
export type ChatRefusal = "forbidden" | "unavailable";

/** A provisional event and when it arrived, for the 60 s expiry. */
export interface ProvisionalEntry {
  event: ProvisionalChatEvent;
  receivedAt: number;
}

export interface ChatStreamState {
  /** Confirmed events, ascending by seq, unique. */
  events: ChatEvent[];
  lastSeq: number;
  /**
   * The contiguity cursor: every seq from `oldestLoadedSeq` through `cursor`
   * is covered — held here, or elided by the server inside a `coverage`
   * interval. A subscription resumes from it, and nothing below it is ever
   * requested again. Only `coverage` (history pages and replay frames) and a
   * dense run of live events move it; the count of events never does.
   */
  cursor: number;
  /** First seq of the loaded window; null before anything was loaded. A
   * tail-first open leaves everything before it unloaded, not covered. */
  oldestLoadedSeq: number | null;
  /** Older history exists that is not loaded, paged with `beforeSeq`. */
  hasMoreBefore: boolean;
  loaded: boolean;
  /** True once oldest events have been trimmed from memory. */
  trimmed: boolean;
  sync: ChatSyncState;
  /** When `sync` last changed: a chat not live for 2 s reads "Catching up…". */
  syncSince: number;
  /** The relay refused this chat for good (a non-retryable
   * `chat.subscribe_failed`, review #15): it says so instead of "Catching
   * up…" forever. Cleared by the next live replay. */
  refusal?: ChatRefusal;
  /** Unconfirmed events from this machine's worker (LocalApi), keyed by
   * eventId, in arrival order. Never persisted, never part of the confirmed
   * fold: they are folded as a suffix over a copy of it. */
  provisional: readonly ProvisionalEntry[];
}

/** A history page or a replay frame: events plus the interval scanned. */
export interface CoveredEvents {
  events: ChatEvent[];
  coverage: ChatEventsCoverage | null;
  /** Older history exists before `coverage.fromSeq` (a page's
   * `hasMoreBefore`, or a replay frame's `hasGapBefore`). */
  hasMoreBefore: boolean;
  /** What to do when the interval starts past the cursor with a hole in
   * between. A replay restarts the window at it (`reset`, the default). A
   * tail page racing a replay that is already filling the window is dropped
   * (`ignore`): the replay reaches the same newest events contiguously. */
  onHole?: "reset" | "ignore";
}

/** Confirmed events that just landed, for layers that reconcile against them
 * (optimistic sends). Called inside the same store update. */
type ConfirmedListener = (chatId: string, events: readonly ChatEvent[]) => void;
const confirmedListeners = new Set<ConfirmedListener>();
export function onConfirmedEvents(listener: ConfirmedListener): () => void {
  confirmedListeners.add(listener);
  return () => confirmedListeners.delete(listener);
}

interface ChatStoreState {
  chats: Record<string, ChatStreamState>;
  /** Replace the stream with a known-contiguous run of events (a cache read). */
  restore: (chatId: string, snapshot: Pick<ChatStreamState, "events" | "cursor" | "oldestLoadedSeq" | "hasMoreBefore">) => void;
  /** Set (replace) the full event log for a chat. Treats `events` as the
   * whole history: tests and callers holding a complete transcript. */
  setInitial: (chatId: string, events: ChatEvent[]) => void;
  /** A history page or replay frame carrying `coverage`. */
  applyCovered: (chatId: string, page: CoveredEvents) => void;
  /** Live events (no coverage). The cursor only moves across a dense run. */
  mergeEvents: (chatId: string, incoming: ChatEvent[]) => void;
  applyProvisional: (chatId: string, events: readonly ProvisionalChatEvent[], now?: number) => void;
  /** Drop every provisional of one chat (unsubscribe), or of every chat
   * (local disconnect). */
  dropProvisional: (chatId?: string) => void;
  /** Drop provisionals the relay has not confirmed within the TTL. The TTL
   * only runs while the relay is open (`relayOpenSince`; null while it is
   * not): with the relay down the provisionals are all there is (web #10). */
  expireProvisional: (now?: number, relayOpenSince?: number | null) => void;
  /** `synchronizing` suspends provisional admission until `live`: a replay
   * elides deltas it cannot then confirm. The ones already shown stay until
   * the replay covers their job. */
  setSync: (chatId: string, sync: ChatSyncState, now?: number) => void;
  /** A non-retryable subscribe refusal; `setSync(…, "live")` clears it. */
  refuse: (chatId: string, refusal: ChatRefusal) => void;
  /** Mark an already-loaded stream as recently viewed without changing it. */
  touch: (chatId: string) => void;
  reset: (chatId: string) => void;
  /** Drop all chats' state, e.g. when the org socket switches orgs. */
  resetAll: () => void;
}

/** Stable reference so zustand's `useSyncExternalStore` snapshot never looks
 * "new" for chats we haven't loaded yet (avoids an infinite-loop warning). */
const EMPTY_PROVISIONAL: readonly ProvisionalEntry[] = [];
export const EMPTY_STREAM: ChatStreamState = {
  events: [], lastSeq: 0, cursor: 0, oldestLoadedSeq: null, hasMoreBefore: false,
  loaded: false, trimmed: false, sync: "synchronizing", syncSince: 0, provisional: EMPTY_PROVISIONAL,
};

/** A stream this store has just started holding: not live yet, as of now. */
function freshStream(): ChatStreamState {
  return { ...EMPTY_STREAM, syncSince: Date.now() };
}

// -- pure merge ---------------------------------------------------------------

function isSortedUnique(events: readonly ChatEvent[]): boolean {
  for (let index = 1; index < events.length; index++) {
    if (events[index]!.seq <= events[index - 1]!.seq) return false;
  }
  return true;
}

/**
 * Merges `incoming` into a sorted, unique event list.
 *
 * The append fast path is the streaming case — every live frame is newer than
 * everything held — and costs one concat, no map and no re-sort. Anything else
 * (a replay page landing behind a live event, an older page) takes the general
 * path. Returns the same array when nothing was new.
 */
export function mergeSorted(current: ChatEvent[], incoming: readonly ChatEvent[]): ChatEvent[] {
  if (incoming.length === 0) return current;
  const ordered = isSortedUnique(incoming) ? incoming : dedupeSorted([...incoming].sort((a, b) => a.seq - b.seq));
  const last = current[current.length - 1];
  if (!last || ordered[0]!.seq > last.seq) return current.length === 0 ? [...ordered] : current.concat(ordered);
  const bySeq = new Map(current.map((event) => [event.seq, event]));
  let changed = false;
  for (const event of ordered) {
    if (bySeq.has(event.seq)) continue;
    bySeq.set(event.seq, event);
    changed = true;
  }
  if (!changed) return current;
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

function dedupeSorted(events: ChatEvent[]): ChatEvent[] {
  return events.filter((event, index) => index === 0 || event.seq !== events[index - 1]!.seq);
}

/** Index of the first event with seq > `seq`. */
function upperBound(events: readonly ChatEvent[], seq: number): number {
  let low = 0;
  let high = events.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (events[mid]!.seq <= seq) low = mid + 1; else high = mid;
  }
  return low;
}

/** Walks the cursor over held events that follow it densely. Live events are
 * never elided, so a gap in their seqs is a real gap and stops the walk. */
function advanceOverDenseRun(events: readonly ChatEvent[], cursor: number): number {
  let next = cursor;
  for (let index = upperBound(events, cursor); index < events.length; index++) {
    if (events[index]!.seq !== next + 1) break;
    next += 1;
  }
  return next;
}

type Window = Pick<ChatStreamState, "events" | "cursor" | "oldestLoadedSeq" | "hasMoreBefore">;

/**
 * Applies a covered read to the loaded window.
 *
 * `coverage` is a cursor override: the cursor becomes
 * `max(cursor, coverage.throughSeq)` whenever the interval touches the window,
 * so an elided delta is never "missing". An interval that starts past the
 * cursor with a hole in between (a `maxReplay`-bounded replay after a long
 * absence) cannot be joined to what is held: the window restarts at it, the
 * older events are dropped, and the history before it is left to `beforeSeq`
 * paging. That keeps the transcript one contiguous run with no silent gap.
 */
export function applyCoverage(window: Window, page: CoveredEvents): Window {
  const coverage = page.coverage;
  if (!coverage) {
    // Nothing scanned: an empty chat, or nothing older than the window. Either
    // way the page's own flag is the truth about what lies before.
    const events = mergeSorted(window.events, page.events);
    return { ...window, events, oldestLoadedSeq: window.oldestLoadedSeq ?? 0, hasMoreBefore: page.hasMoreBefore };
  }
  const { fromSeq, throughSeq } = coverage;
  const { oldestLoadedSeq, cursor } = window;
  if (oldestLoadedSeq === null) {
    // Nothing loaded yet: the page defines the window. Live events that raced
    // ahead of it are kept when they sit inside or just past it.
    const kept = window.events.filter((event) => event.seq >= fromSeq);
    const events = mergeSorted(kept, page.events);
    return {
      events,
      cursor: advanceOverDenseRun(events, throughSeq),
      oldestLoadedSeq: fromSeq,
      hasMoreBefore: page.hasMoreBefore,
    };
  }
  if (fromSeq > cursor + 1) {
    if (page.onHole === "ignore") return window;
    // A hole between what is held and this interval.
    const events = mergeSorted(window.events.filter((event) => event.seq >= fromSeq), page.events);
    return {
      events,
      cursor: advanceOverDenseRun(events, throughSeq),
      oldestLoadedSeq: fromSeq,
      hasMoreBefore: true,
    };
  }
  if (throughSeq < oldestLoadedSeq - 1) {
    // An older page that does not reach the window cannot be placed.
    return window;
  }
  const events = mergeSorted(window.events, page.events);
  const extendsBottom = fromSeq < oldestLoadedSeq;
  return {
    events,
    cursor: advanceOverDenseRun(events, Math.max(cursor, throughSeq)),
    oldestLoadedSeq: Math.min(oldestLoadedSeq, fromSeq),
    hasMoreBefore: extendsBottom ? page.hasMoreBefore : window.hasMoreBefore,
  };
}

/** Live events: merged, and the cursor walks a dense run from where it is. A
 * stream with nothing loaded holds them without a cursor until a covered read
 * says where history starts. */
export function applyLive(window: Window, incoming: readonly ChatEvent[]): Window {
  const events = mergeSorted(window.events, incoming);
  if (events === window.events) return window;
  if (window.oldestLoadedSeq === null) return { ...window, events };
  return { ...window, events, cursor: advanceOverDenseRun(events, window.cursor) };
}

function trimIfNeeded(window: Window): Window & { didTrim: boolean } {
  if (window.events.length <= MAX_EVENTS) return { ...window, didTrim: false };
  const events = window.events.slice(TRIM_COUNT);
  return { ...window, events, oldestLoadedSeq: events[0]!.seq, hasMoreBefore: true, didTrim: true };
}

/** Provisionals the confirmed events settle: the same eventId arrived, or the
 * job reached a terminal status — or, for a covered read (`covered`), the read
 * carries the job at all: a replay elides superseded deltas, so a provisional
 * delta it covers would never see its eventId confirmed and would sit beside
 * the confirmed text as a duplicate. Promotion is all-or-nothing per frame. */
export function settleProvisional(provisional: readonly ProvisionalEntry[], confirmed: readonly ChatEvent[], covered = false): readonly ProvisionalEntry[] {
  if (provisional.length === 0 || confirmed.length === 0) return provisional;
  const eventIds = new Set<string>();
  const endedJobs = new Set<string>();
  for (const event of confirmed) {
    if (event.eventId) eventIds.add(event.eventId);
    if (event.jobId && (covered || (event.payload.kind === "job.status" && isTerminal(event.payload.status)))) endedJobs.add(event.jobId);
  }
  if (eventIds.size === 0 && endedJobs.size === 0) return provisional;
  const kept = provisional.filter((entry) => !eventIds.has(entry.event.eventId)
    && !(entry.event.jobId !== undefined && endedJobs.has(entry.event.jobId)));
  return kept.length === provisional.length ? provisional : kept.length === 0 ? EMPTY_PROVISIONAL : kept;
}

function isTerminal(status: string): boolean {
  return status === "completed" || status === "failed" || status === "canceled";
}

/** Keep recently viewed conversations warm so returning to a workspace is an
 * in-memory selection, while bounding the worst-case footprint. Re-inserting
 * the selected id moves it to the end of the object's insertion order (LRU). */
function retainChat(
  chats: Record<string, ChatStreamState>,
  chatId: string,
  stream: ChatStreamState,
): Record<string, ChatStreamState> {
  const retained = { ...chats };
  delete retained[chatId];
  retained[chatId] = stream;
  let overflow = Object.keys(retained).length - MAX_RETAINED_CHATS;
  if (overflow > 0) {
    // A subscribed chat is never evicted: it is on screen (or kept warm on
    // purpose), and nothing would refill it — its subscription is already up.
    // Only unheld entries (hover prefetches, chats left behind) make room.
    for (const id of Object.keys(retained)) {
      if (overflow <= 0) break;
      if (id === chatId || isHeld(id)) continue;
      delete retained[id];
      overflow -= 1;
    }
  }
  return retained;
}

let isHeld: (chatId: string) => boolean = () => false;

/** The subscription layer says which chats are held; those are never evicted. */
export function setRetentionGuard(guard: (chatId: string) => boolean): void {
  isHeld = guard;
}

function withWindow(current: ChatStreamState, next: Window, confirmed: readonly ChatEvent[], covered = false): ChatStreamState {
  const trimmed = trimIfNeeded(next);
  const last = trimmed.events[trimmed.events.length - 1];
  return {
    ...current,
    events: trimmed.events,
    cursor: trimmed.cursor,
    oldestLoadedSeq: trimmed.oldestLoadedSeq,
    hasMoreBefore: trimmed.hasMoreBefore,
    lastSeq: Math.max(current.lastSeq, last?.seq ?? 0),
    // Loaded means a covered read placed the window. Live events alone are a
    // fragment: shown as a transcript they would read as the whole history.
    loaded: trimmed.oldestLoadedSeq !== null,
    trimmed: current.trimmed || trimmed.didTrim,
    provisional: settleProvisional(current.provisional, confirmed, covered),
  };
}

function notifyConfirmed(chatId: string, events: readonly ChatEvent[]) {
  if (events.length === 0) return;
  for (const listener of confirmedListeners) listener(chatId, events);
}

export const useChatStore = create<ChatStoreState>((set) => ({
  chats: {},

  restore: (chatId, snapshot) =>
    set((state) => {
      const current = state.chats[chatId];
      // Memory is at least as new as the disk copy whenever it exists.
      if (current?.loaded) return state;
      const last = snapshot.events[snapshot.events.length - 1];
      // A restored send's echo may already be in the copy (C3).
      notifyConfirmed(chatId, snapshot.events);
      return {
        chats: retainChat(state.chats, chatId, {
          ...EMPTY_STREAM,
          events: snapshot.events,
          cursor: snapshot.cursor,
          oldestLoadedSeq: snapshot.oldestLoadedSeq,
          hasMoreBefore: snapshot.hasMoreBefore,
          lastSeq: last?.seq ?? 0,
          loaded: true,
          sync: "cached",
          syncSince: Date.now(),
        }),
      };
    }),

  setInitial: (chatId, events) =>
    set((state) => {
      const sorted = dedupeSorted([...events].sort((a, b) => a.seq - b.seq));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const window: Window = {
        events: sorted,
        cursor: last?.seq ?? 0,
        oldestLoadedSeq: first ? first.seq : 0,
        hasMoreBefore: false,
      };
      const current = state.chats[chatId] ?? freshStream();
      const next = withWindow({ ...current, lastSeq: 0, trimmed: false }, window, sorted);
      notifyConfirmed(chatId, sorted);
      return { chats: retainChat(state.chats, chatId, next) };
    }),

  applyCovered: (chatId, page) =>
    set((state) => {
      const current = state.chats[chatId] ?? freshStream();
      const window = applyCoverage(current, page);
      notifyConfirmed(chatId, page.events);
      return { chats: retainChat(state.chats, chatId, withWindow(current, window, page.events, page.coverage !== null)) };
    }),

  mergeEvents: (chatId, incoming) =>
    set((state) => {
      if (incoming.length === 0) return state;
      const current = state.chats[chatId] ?? freshStream();
      const window = applyLive(current, incoming);
      notifyConfirmed(chatId, incoming);
      if (window === current && current.provisional.length === 0) return state;
      return { chats: retainChat(state.chats, chatId, withWindow(current, window, incoming)) };
    }),

  applyProvisional: (chatId, events, now = Date.now()) =>
    set((state) => {
      const current = state.chats[chatId];
      // Provisionals only ever extend a stream the relay is already feeding,
      // and never during a replay: the replay elides superseded deltas, so a
      // provisional delta it covers would never see its eventId confirmed
      // and would sit beside the confirmed text as a duplicate.
      if (!current?.loaded || current.sync !== "live" || events.length === 0) return state;
      const known = new Set(current.provisional.map((entry) => entry.event.eventId));
      // A confirmation can beat its own provisional here; scan the recent tail.
      for (let index = Math.max(0, current.events.length - 500); index < current.events.length; index++) {
        const eventId = current.events[index]!.eventId;
        if (eventId) known.add(eventId);
      }
      const added = events.filter((event) => !known.has(event.eventId)).map((event) => ({ event, receivedAt: now }));
      if (added.length === 0) return state;
      return { chats: { ...state.chats, [chatId]: { ...current, provisional: [...current.provisional, ...added] } } };
    }),

  dropProvisional: (chatId) =>
    set((state) => {
      const ids = chatId === undefined ? Object.keys(state.chats) : [chatId];
      let chats: Record<string, ChatStreamState> | null = null;
      for (const id of ids) {
        const stream = state.chats[id];
        if (!stream || stream.provisional.length === 0) continue;
        chats ??= { ...state.chats };
        chats[id] = { ...stream, provisional: EMPTY_PROVISIONAL };
      }
      return chats ? { chats } : state;
    }),

  expireProvisional: (now = Date.now(), relayOpenSince = 0) =>
    set((state) => {
      if (relayOpenSince === null) return state;
      let chats: Record<string, ChatStreamState> | null = null;
      for (const [id, stream] of Object.entries(state.chats)) {
        if (stream.provisional.length === 0) continue;
        const kept = stream.provisional.filter((entry) => now - Math.max(entry.receivedAt, relayOpenSince) < LOCAL_PROVISIONAL_TTL_MS);
        if (kept.length === stream.provisional.length) continue;
        chats ??= { ...state.chats };
        chats[id] = { ...stream, provisional: kept.length === 0 ? EMPTY_PROVISIONAL : kept };
      }
      return chats ? { chats } : state;
    }),

  setSync: (chatId, sync, now = Date.now()) =>
    set((state) => {
      const stream = state.chats[chatId];
      if (!stream) return state;
      if (stream.sync === sync) {
        if (sync !== "live" || !stream.refusal) return state;
        return { chats: { ...state.chats, [chatId]: { ...stream, refusal: undefined } } };
      }
      if (sync === "live" && stream.refusal) {
        return { chats: { ...state.chats, [chatId]: { ...stream, sync, syncSince: now, refusal: undefined } } };
      }
      // A relay replay is starting: new provisionals are refused until it
      // completes (`live`). Those already shown stay — the local daemon may
      // be all that is streaming while the relay is down — and the replay
      // settles the ones whose job it carries (`settleProvisional`).
      return { chats: { ...state.chats, [chatId]: { ...stream, sync, syncSince: now } } };
    }),

  touch: (chatId) =>
    set((state) => {
      const stream = state.chats[chatId];
      return stream ? { chats: retainChat(state.chats, chatId, stream) } : state;
    }),

  reset: (chatId) =>
    set((state) => {
      const next = { ...state.chats };
      delete next[chatId];
      return { chats: next };
    }),

  refuse: (chatId, refusal) =>
    set((state) => {
      const stream = state.chats[chatId];
      if (!stream || stream.refusal === refusal) return state;
      return { chats: { ...state.chats, [chatId]: { ...stream, refusal } } };
    }),

  resetAll: () => set({ chats: {} }),
}));

export function selectChatStream(chatId: string) {
  return (state: ChatStoreState) => state.chats[chatId] ?? EMPTY_STREAM;
}
